'use client'

import { useEffect, useLayoutEffect, useRef, useState } from 'react'

type Props = {
  children: React.ReactNode
  className?: string
  /** vitesse en pixels/seconde — défaut 40 */
  speed?: number
  /** pause initiale avant que le défilement reparte */
  pauseMs?: number
}

/**
 * Affiche son contenu sur une seule ligne.
 * - Si le texte tient : aucun défilement.
 * - Si le texte déborde : défile en boucle continue (Spotify-style), avec une
 *   copie répétée pour un wrap propre, et un fondu sur les bords.
 *
 * On rend TOUJOURS le contenu en inline-block whitespace-nowrap (pas de
 * truncate), donc on peut mesurer la largeur naturelle du texte. Le wrapper a
 * overflow:hidden pour clipper si nécessaire.
 */
export default function Marquee({
  children, className = '', speed = 40, pauseMs = 1500,
}: Props) {
  const wrapRef = useRef<HTMLDivElement | null>(null)
  const innerRef = useRef<HTMLSpanElement | null>(null)
  const [overflow, setOverflow] = useState(false)
  const [duration, setDuration] = useState(0)

  // measure() est volontairement non-stable (capture speed via closure), mais
  // on l'appelle UNIQUEMENT depuis :
  //   - un useLayoutEffect avec deps strictes (children + speed) → 1 fois par
  //     vraie variation, pas à chaque render
  //   - le ResizeObserver / window.resize
  // Important : on QUANTISE duration (arrondi à 0.1s) pour éviter qu'une
  // fluctuation sub-pixel de scrollWidth (font-loading, re-layout) fasse osciller
  // l'état à l'infini → c'était la cause probable du React #185 sur mobile
  // (les 4 Marquees de la PlayerBar re-render à chaque tick de position).
  const measure = () => {
    const wrap = wrapRef.current
    const inner = innerRef.current
    if (!wrap || !inner) return
    const innerW = inner.scrollWidth
    const wrapW = wrap.clientWidth
    if (innerW > wrapW + 4) {
      const dist = innerW + 48
      const newDur = Math.round(Math.max(6, dist / Math.max(20, speed)) * 10) / 10
      setOverflow(prev => prev === true ? prev : true)
      setDuration(prev => prev === newDur ? prev : newDur)
    } else {
      setOverflow(prev => prev === false ? prev : false)
      setDuration(prev => prev === 0 ? prev : 0)
    }
  }

  // Re-mesure quand le contenu change (children) ou la vitesse.
  // PAS de useLayoutEffect sans deps : c'était la cause d'une boucle setState
  // sur mobile (fluctuation de scrollWidth entre rendus).
  useLayoutEffect(() => { measure() }, [children, speed])

  useEffect(() => {
    if (typeof window === 'undefined') return
    const onResize = () => measure()
    window.addEventListener('resize', onResize)
    const wrap = wrapRef.current
    const inner = innerRef.current
    // ResizeObserver throttlé : évite les setState en cascade quand le DOM
    // se stabilise (transitions, font-load).
    let rafId = 0
    const ro = new ResizeObserver(() => {
      if (rafId) return
      rafId = requestAnimationFrame(() => { rafId = 0; measure() })
    })
    if (wrap) ro.observe(wrap)
    if (inner) ro.observe(inner)
    return () => {
      window.removeEventListener('resize', onResize)
      ro.disconnect()
      if (rafId) cancelAnimationFrame(rafId)
    }
  }, [])

  return (
    <div
      ref={wrapRef}
      // select-none : empêche la surbrillance bleu clair (sélection de texte)
      // qui apparaissait par-dessus le titre quand il défile / qu'on le touche.
      className={`relative block w-full max-w-full overflow-hidden select-none ${overflow ? 'blc-marquee-mask' : ''} ${className}`}
    >
      <div
        className={overflow
          ? 'inline-flex whitespace-nowrap blc-marquee-anim'
          : 'block whitespace-nowrap'}
        style={overflow ? {
          animationDuration: `${duration}s`,
          animationDelay: `${pauseMs}ms`,
        } : undefined}
      >
        <span
          ref={innerRef}
          className={overflow ? 'inline-block pr-12' : 'inline-block'}
        >
          {children}
        </span>
        {overflow && (
          <span aria-hidden="true" className="inline-block pr-12">
            {children}
          </span>
        )}
      </div>
    </div>
  )
}
